import { ButtonType, ButtonWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { errorPageDivId } from '../../app/layout/SkipBarProvider';
import { Messages } from '../../codegen/Localize';
import { BrowserTitleService } from '../../commonInfra/services/BrowserTitleService';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { CommonErrorCode } from '../../ts/helpers/ErrorCodes';
import { IRequestAccessForContentParams, RequestAccessForContentRequest } from '../../ts/ServerApi/requests/RequestAccessForContentRequest';
import { sendRequestAccessClickedTelemetry,sendRequestAccessDisplayedTelemetry } from '../telemetry/RequestAccessTelemetry';

export type ContentTypeForRequestAccess = 'project' | 'view' | 'workbook'| 'datasource' | 'metric' | 'lens' | 'flow' | 'publishedConnection' | 'collection';

export interface RequestAccessErrorState {
    firstMessage: string;
    secondMessage: string;
    canRequestAccess: boolean;
}

export interface RequestAccessErrorProps {
  readonly contentType: ContentTypeForRequestAccess;
  readonly contentIdentifier: string;
  readonly url: string;
  readonly errorCode: number;
}

// A class to give the user the option to request access from the owner of the content
export class RequestAccessError extends React.PureComponent<RequestAccessErrorProps, RequestAccessErrorState> {
  public constructor(props: RequestAccessErrorProps) {
      super(props);
      if (props.errorCode === CommonErrorCode.CallingUserInsufficientPermissions) {
        sendRequestAccessDisplayedTelemetry(props.contentType);
        this.state = {firstMessage: Messages.pageUnavailable(), secondMessage: Messages.requestAccessPrompt(), canRequestAccess: true};
      } else {
        this.state = {firstMessage: Messages.pageUnavailable(), secondMessage: Messages.contentDoesntExist(), canRequestAccess: false};
      }
  }

  public componentDidUpdate(prevProps: RequestAccessErrorProps) {
    if (prevProps.errorCode !== this.props.errorCode||
        prevProps.contentIdentifier !== this.props.contentIdentifier ||
        prevProps.contentType !== this.props.contentType) {
        // if we are displaying this page for a new item, we need to reset all the messages

      if (this.props.errorCode === CommonErrorCode.CallingUserInsufficientPermissions) {
        sendRequestAccessDisplayedTelemetry(this.props.contentType);
        if (this.props.contentType === 'lens') {
          this.setState({firstMessage: Messages.pageUnavailable(), secondMessage: Messages.requestAccessPrompt(), canRequestAccess: false});
        } else {
            this.setState({firstMessage: Messages.pageUnavailable(), secondMessage: Messages.requestAccessPrompt(), canRequestAccess: true});
          }
      } else {
        this.setState({firstMessage: Messages.pageUnavailable(), secondMessage: Messages.contentDoesntExist(), canRequestAccess: false});
      }
    }
  }

  private readonly submitRequestAccess = (): void => {
    sendRequestAccessClickedTelemetry(this.props.contentType);

    const params: IRequestAccessForContentParams = {
      contentIdentifier: this.props.contentIdentifier,
      contentType: this.props.contentType
    };

    WebHttpClient.sendRequest(new RequestAccessForContentRequest(params))
      .then((result: IResult) => {
        const error = result.errors;
        if (error) {
          this.setState({firstMessage: Messages.requestAccessError(), secondMessage: Messages.requestAccessErrorDescription(), canRequestAccess: false});
        } else {
          this.setState({firstMessage: Messages.requestAccessSubmitted(), secondMessage: Messages.requestAccessOwnerNotified(), canRequestAccess: false});
        }
      });
   }

   public componentDidMount() {
     BrowserTitleService.setTitleToServerName();
   }


  public render(): JSX.Element {
    return (
      <div style={{height: 'calc(100vh - 15%)', flex: '1', position: 'relative', outline: 'none'}} id={errorPageDivId} tabIndex={-1}>
      <div className="tb-error-box">
       <div role="alert" aria-atomic="true">
         <div className="tb-error-box-message-first">{this.state.firstMessage}</div>
         <div className="tb-error-box-message-second">{this.state.secondMessage}</div>
       </div>
        {this.state.canRequestAccess &&
            <ButtonWidget style={{marginBottom: '28px'}} buttonType={ButtonType.Outline} handleClick={this.submitRequestAccess}
            testId={'request-access-submit-btn'}>{Messages.requestAccess()}</ButtonWidget>
        }
        <br/>
        <TextLinkWidget href={this.props.url} testId={'request-access-explore-link'}>{Messages.exploreOtherContent()}</TextLinkWidget>
       </div>
     </div>
    );
  }
}
